import { AgentInstructionSchema, AgentSchema, } from '@cloneai/shared-types';
import { AgentVoiceCloneActionResponseSchema, AgentVoiceCaptureActionResponseSchema, AgentVoiceCaptureCreateRequestSchema, AgentVoiceCaptureCreateResponseSchema, AgentVoiceCloneSetupRequestSchema, AgentVoiceCloneSetupResponseSchema, AgentVoiceCloneStatusResponseSchema, AgentDetailSchema, AgentKnowledgeConfigSchema, AgentKnowledgeConfigUpdateRequestSchema, AgentKnowledgeFileSourceUploadRequestSchema, AgentKnowledgeFileSourceUploadResponseSchema, AgentKnowledgeListResponseSchema, AgentKnowledgeSourceDetailResponseSchema, AgentKnowledgeSourceResponseSchema, AgentKnowledgeSourceUpdateRequestSchema, AgentKnowledgeTextSourceCreateRequestSchema, AgentMediaUploadResponseSchema, AgentMetricsResponseSchema, AgentSummarySchema, SessionDetailResponseSchema, SessionListResponseSchema, } from '@cloneai/shared-types/api';
import { z } from 'zod';
import { NoContentSchema } from './_helpers.js';
// Composed locally — a list-of-agents response schema is not exported by
// shared-types (P2-01 keeps the per-item schema atomic). Wrapping here lets
// the http layer's Zod check run against the actual wire shape.
const AgentListResponseSchema = z.object({
    items: z.array(AgentSummarySchema),
    nextCursor: z.string().nullable(),
});
// Matches the `AgentInstruction.body` constraint from docs/data-model.md §4.7.
const AgentInstructionPutRequestSchema = z.object({
    body: z.string().min(1).max(16384),
});
export function createAgentsResource(http) {
    return {
        list: (query) => http.request({
            method: 'GET',
            path: '/v1/tenant/agents',
            query: query,
            responseSchema: AgentListResponseSchema,
        }),
        create: (body) => http.request({
            method: 'POST',
            path: '/v1/tenant/agents',
            body,
            responseSchema: AgentSummarySchema,
        }),
        get: (agentId) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}`,
            responseSchema: AgentDetailSchema,
        }),
        update: (agentId, body) => http.request({
            method: 'PATCH',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}`,
            body,
            responseSchema: AgentDetailSchema,
        }),
        remove: async (agentId) => {
            await http.request({
                method: 'DELETE',
                path: `/v1/tenant/agents/${encodeURIComponent(agentId)}`,
                responseSchema: NoContentSchema,
            });
        },
        activate: (agentId) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/activate`,
            responseSchema: AgentSchema,
        }),
        deactivate: (agentId) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/deactivate`,
            responseSchema: AgentSchema,
        }),
        activateLanguage: (agentId, lang) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/languages/${encodeURIComponent(lang)}/activate`,
            responseSchema: AgentSchema,
        }),
        deactivateLanguage: (agentId, lang) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/languages/${encodeURIComponent(lang)}/deactivate`,
            responseSchema: AgentSchema,
        }),
        getInstruction: (agentId, lang) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/instructions/${encodeURIComponent(lang)}`,
            responseSchema: AgentInstructionSchema,
        }),
        putInstruction: async (agentId, lang, body) => {
            // Validate the body on the client side so misuse surfaces here instead
            // of as a 400 from the server.
            const parsed = AgentInstructionPutRequestSchema.parse(body);
            await http.request({
                method: 'PUT',
                path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/instructions/${encodeURIComponent(lang)}`,
                body: parsed,
                responseSchema: NoContentSchema,
            });
        },
        requestMediaUploadUrl: (agentId, body) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/media/upload-url`,
            body,
            responseSchema: AgentMediaUploadResponseSchema,
        }),
        deleteBackground: async (agentId, mediaId) => {
            await http.request({
                method: 'DELETE',
                path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/backgrounds/${encodeURIComponent(mediaId)}`,
                responseSchema: NoContentSchema,
            });
        },
        getVoiceClone: (agentId) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone`,
            responseSchema: AgentVoiceCloneStatusResponseSchema,
        }),
        setupVoiceClone: (agentId, body) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone/setup`,
            body: AgentVoiceCloneSetupRequestSchema.parse(body),
            responseSchema: AgentVoiceCloneSetupResponseSchema,
        }),
        createVoiceCapture: (agentId, body) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone/capture`,
            body: AgentVoiceCaptureCreateRequestSchema.parse(body),
            responseSchema: AgentVoiceCaptureCreateResponseSchema,
        }),
        revokeVoiceCapture: (agentId) => http.request({
            method: 'DELETE',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone/capture`,
            responseSchema: AgentVoiceCaptureActionResponseSchema,
        }),
        activateVoiceClone: (agentId) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone/activate`,
            responseSchema: AgentVoiceCloneActionResponseSchema,
        }),
        deactivateVoiceClone: (agentId) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/voice-clone/deactivate`,
            responseSchema: AgentVoiceCloneActionResponseSchema,
        }),
        listSessions: (agentId, query) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/sessions`,
            query: query,
            responseSchema: SessionListResponseSchema,
        }),
        getSession: (sessionId) => http.request({
            method: 'GET',
            path: `/v1/tenant/sessions/${encodeURIComponent(sessionId)}`,
            responseSchema: SessionDetailResponseSchema,
        }),
        deleteSession: async (sessionId) => {
            await http.request({
                method: 'DELETE',
                path: `/v1/tenant/sessions/${encodeURIComponent(sessionId)}`,
                responseSchema: NoContentSchema,
            });
        },
        metrics: (agentId, query) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/metrics`,
            query: query,
            responseSchema: AgentMetricsResponseSchema,
        }),
        getKnowledge: (agentId) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge`,
            responseSchema: AgentKnowledgeListResponseSchema,
        }),
        updateKnowledgeConfig: (agentId, body) => http.request({
            method: 'PATCH',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/config`,
            body: AgentKnowledgeConfigUpdateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeConfigSchema,
        }),
        createKnowledgeTextSource: (agentId, body) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/sources`,
            body: AgentKnowledgeTextSourceCreateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeSourceResponseSchema,
        }),
        getKnowledgeSource: (agentId, sourceId) => http.request({
            method: 'GET',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
            responseSchema: AgentKnowledgeSourceDetailResponseSchema,
        }),
        updateKnowledgeSource: (agentId, sourceId, body) => http.request({
            method: 'PATCH',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
            body: AgentKnowledgeSourceUpdateRequestSchema.parse(body),
            responseSchema: AgentKnowledgeSourceResponseSchema,
        }),
        requestKnowledgeFileUploadUrl: (agentId, body) => http.request({
            method: 'POST',
            path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/files/upload-url`,
            body: AgentKnowledgeFileSourceUploadRequestSchema.parse(body),
            responseSchema: AgentKnowledgeFileSourceUploadResponseSchema,
        }),
        deleteKnowledgeSource: async (agentId, sourceId) => {
            await http.request({
                method: 'DELETE',
                path: `/v1/tenant/agents/${encodeURIComponent(agentId)}/knowledge/sources/${encodeURIComponent(sourceId)}`,
                responseSchema: NoContentSchema,
            });
        },
    };
}
//# sourceMappingURL=agents.js.map